import { defineStore } from 'pinia';
import { ref } from 'vue';
import { AxiosError } from 'axios';

import { ApiResponse } from '@/shared/types/api';
import invitationsApi from '@/features/household/api/invitation';

import {
  HouseholdInvitation,
  HouseholdInvitationWithHouseholdName,
} from '@/features/household/models/invitation';
import {
  CreateHouseholdInvitationForm,
  AcceptHouseholdInvitationForm,
  DeclineHouseholdInvitationForm,
  RevokeHouseholdInvitationForm
} from '@/features/household/form/invitation_form';

export const useHouseholdInvitationStore = defineStore('householdInvitationStore', () => {
  const pendingInvitation = ref<HouseholdInvitationWithHouseholdName | null>(null);
  const invitations = ref<HouseholdInvitation[]>([]);
  const isLoading = ref(false);
  
  function initInvitations(data: HouseholdInvitation[]) {
    invitations.value = data;
  }

  async function getPendingInvitation(): Promise<ApiResponse<HouseholdInvitationWithHouseholdName>> {
    try {
      const response = await invitationsApi.getPendingInvitation();
      if (response.status === 'success') {
        pendingInvitation.value = response.data;
        return response;
      }
    } catch (error) {
      console.error('Failed to get pending invitation:', error);
      return {
        status: 'error',
        error: {
          code: 400,
          message: 'Failed to get pending invitation',
        },
      };
    }
    return {
      status: 'error',
      error: {
        code: 400,
        message: 'Failed to get pending invitation',
      }
    };
  }

  async function getInvitationByToken(token: string): Promise<ApiResponse<HouseholdInvitationWithHouseholdName>> {
    try {
      const response = await invitationsApi.getInvitationByToken(token);
      if (response.status === 'success') {
        return response;
      }
    } catch (error) {
      console.error('Failed to get invitation by token:', error);
      return {
        status: 'error',
        error: {
          code: 400,
          message: 'Failed to get invitation by token',
        }
      };
    }
    return {
      status: 'error',
      error: {
        code: 400,
        message: 'Failed to get invitation by token',
      }
    };
  }
  
  async function getInvitations(): Promise<ApiResponse<HouseholdInvitation[]>> {
    try {
      const response = await invitationsApi.getHouseholdInvitations();

      if (response.status === 'success') {
        initInvitations(response.data);

        return {
          status: 'success',
          data: response.data,
        };
      }
    } catch (error) {
      const _error = error as AxiosError<string>;
      return {
        status: 'error',
        error: {
          code: _error.response?.status || 500,
          message: 'Failed to get invitations',
        },
      };
    }
    return {
      status: 'error',
      error: {
        code: 400,
        message: 'Failed to get invitations',
      }
    };
  }

  async function createInvitation(form: CreateHouseholdInvitationForm): Promise<void> {
    try {
      isLoading.value = true;

      const response = await invitationsApi.createHouseholdInvitation(form);
      if (response.status === 'success') {
        await getInvitations(); // Refresh the invitations list
      }
    } catch (error) {
      console.error('Failed to send invitation:', error);
    } finally {
      isLoading.value = false;
    }
  }

  async function revokeInvitation(form: RevokeHouseholdInvitationForm): Promise<void> {
    try {
      isLoading.value = true;
      const response = await invitationsApi.revokeHouseholdInvitation(form);
      if (response.status === 'success') {
        await getInvitations(); // Refresh the invitations list
      }
    } catch (error) {
      console.error('Failed to revoke invitation:', error);
    } finally {
      isLoading.value = false;
    }
  }

  async function acceptInvitation(form: AcceptHouseholdInvitationForm): Promise<void> {
    try {
      isLoading.value = true;
      await invitationsApi.acceptHouseholdInvitation(form);
    } catch (error) {
      console.error('Failed to accept invitation:', error);
    } finally {
      isLoading.value = false;
    }
  }

  async function declineInvitation(form: DeclineHouseholdInvitationForm): Promise<void> {
    try {
      isLoading.value = true;
      await invitationsApi.declineHouseholdInvitation(form);
    } catch (error) {
      console.error('Failed to decline invitation:', error);
    } finally {
      isLoading.value = false;
    }
  }

  return {
    pendingInvitation,
    invitations,
    isLoading,
    getPendingInvitation,
    getInvitationByToken,
    getInvitations,
    createInvitation,
    revokeInvitation,
    acceptInvitation,
    declineInvitation,
  }
})
